import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { DensityNote } from '@/components/px/DensityNote'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { KpiCard } from '@/components/px/KpiCard'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  descriptorsRetryClicked,
  fetchAgentDescriptors,
  selectAgentDescriptors,
  selectDescriptorsCache,
  selectDescriptorsError,
} from '@/features/descriptors/descriptorsSlice'
import type { AgentDescriptor } from '@/features/descriptors/types'
import {
  firmEffectiveRefetchArmed,
  firmEffectiveKeyOf,
  loadFirmEffectiveCollection,
  selectFirmEffectiveError,
  selectFirmEffectiveLoading,
  selectFirmEffectiveMap,
  type FirmEffectiveRequest,
} from '@/features/agentsettings/slices/agentSettingsSlice'
import {
  AGENT_PHASES,
  type FirmEffectiveAgentSettings,
} from '@/features/agentsettings/types'
import { useAgentSettingsLabels } from '@/features/agentsettings/agentSettingsLabels'
import {
  agentsRefetchArmed,
  openAgents,
  selectAgents,
  selectAgentsError,
  selectAgentsLoading,
} from '@/features/agents/slices/agentsSlice'
import type { AgentView } from '@/features/agents/types'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store'

const HEAD_CLASS =
  'text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

interface CatalogueRow {
  agent: AgentView
  descriptor?: AgentDescriptor
}

const catalogueRows = (
  agents: readonly AgentView[],
  descriptors: readonly AgentDescriptor[]
): CatalogueRow[] => {
  const agentsById = new Map(agents.map((agent) => [agent.id, agent]))
  const matched = descriptors.flatMap((descriptor) => {
    const agent = agentsById.get(descriptor.key)
    return agent === undefined ? [] : [{ agent, descriptor }]
  })
  const descriptorKeys = new Set(
    descriptors.map((descriptor) => descriptor.key)
  )
  return [
    ...matched,
    ...agents
      .filter((agent) => !descriptorKeys.has(agent.id))
      .map((agent) => ({ agent })),
  ]
}

function AutonomyCell({
  requests,
}: {
  requests: readonly FirmEffectiveRequest[]
}) {
  const { t } = useTranslation('agents')
  const labels = useAgentSettingsLabels()
  const effectiveMap = useAppSelector(selectFirmEffectiveMap)
  const effective = useMemo(
    () =>
      requests.flatMap((request) => {
        const value = effectiveMap[firmEffectiveKeyOf(request)]
        return value === undefined ? [] : [[request.phase, value] as const]
      }),
    [effectiveMap, requests]
  )
  const loading = useAppSelector(selectFirmEffectiveLoading)
  const error = useAppSelector(selectFirmEffectiveError)

  if (loading && effective.length === 0)
    return (
      <span className="text-xs text-muted-foreground">
        {t('catalogue.loading')}
      </span>
    )
  if (error !== null || effective.length === 0)
    return (
      <span className="text-xs text-muted-foreground">
        {t('catalogue.effectiveUnavailable')}
      </span>
    )

  return (
    <ul className="space-y-1" aria-label={t('catalogue.phaseAutonomy')}>
      {effective.map(([phase, row]) => (
        <li key={phase} className="text-xs text-ink-soft">
          <span className="font-medium text-foreground">
            {t(`phases.${phase}`)}
          </span>{' '}
          <span data-rung={row.autonomyRung}>
            {labels.rung(row.autonomyRung)}
          </span>{' '}
          <span className="text-muted-foreground">
            · {labels.provenance(row.provenance.autonomyRung ?? 'SYSTEM_DEFAULT')}
          </span>
        </li>
      ))}
    </ul>
  )
}

function CatalogueRowView({
  row,
  orgId,
}: {
  row: CatalogueRow
  orgId: string
}) {
  const { t } = useTranslation('agents')
  const labels = useAgentSettingsLabels()
  const lastActive = labels.dateTime(row.agent.metrics?.lastActiveAt)
  const requests = useMemo<readonly FirmEffectiveRequest[]>(
    () =>
      AGENT_PHASES.map((phase) => ({
        orgId,
        phase,
        agentKey: row.agent.id,
      })),
    [orgId, row.agent.id]
  )
  const name = row.descriptor?.name ?? row.agent.name
  const description = row.descriptor?.description ?? row.agent.description
  const category = row.descriptor?.category ?? row.agent.category
  const href = `/library/agents/${encodeURIComponent(row.agent.id)}`

  return (
    <TableRow>
      <TableHead
        scope="row"
        className="h-auto max-w-[16rem] whitespace-normal py-2.5 pl-3 align-top font-normal"
      >
        <Link
          to={href}
          className="text-sm font-medium text-foreground underline underline-offset-2 hover:no-underline"
        >
          {name}
        </Link>
        {category !== undefined && (
          <Badge
            variant="outline"
            className="ml-2 font-normal text-muted-foreground"
          >
            {category}
          </Badge>
        )}
        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
          {row.agent.id}
        </span>
      </TableHead>
      <TableCell className="max-w-[24rem] whitespace-normal py-2.5 align-top text-sm text-ink-soft">
        {description ?? t('catalogue.noDescription')}
      </TableCell>
      <TableCell className="py-2.5 align-top text-sm">
        <Badge variant={row.agent.isConfigured ? 'default' : 'outline'}>
          {row.agent.isConfigured
            ? t('catalogue.configured')
            : t('catalogue.notConfigured')}
        </Badge>
      </TableCell>
      <TableCell className="min-w-[13rem] py-2.5 align-top">
        <AutonomyCell requests={requests} />
      </TableCell>
      <TableCell className="min-w-[9rem] whitespace-normal py-2.5 align-top text-sm text-ink-soft">
        {row.agent.metrics?.status ?? t('catalogue.unavailable')}
        {lastActive !== null && (
          <time
            dateTime={row.agent.metrics?.lastActiveAt}
            className="mt-1 block text-xs text-muted-foreground"
          >
            {lastActive}
          </time>
        )}
      </TableCell>
      <TableCell className="py-2.5 pr-3 text-right align-top">
        <Link
          to={href}
          aria-label={t('catalogue.configureAgent', { agent: name })}
          className="text-sm text-foreground underline underline-offset-2 hover:no-underline"
        >
          {t('catalogue.configure')}
        </Link>
      </TableCell>
    </TableRow>
  )
}

function AutonomyLadder({
  requests,
}: {
  requests: readonly FirmEffectiveRequest[]
}) {
  const { t } = useTranslation('agents')
  const labels = useAgentSettingsLabels()
  const effectiveMap = useAppSelector(selectFirmEffectiveMap)
  const values = useMemo(
    () =>
      requests.flatMap((request) => {
        const value = effectiveMap[firmEffectiveKeyOf(request)]
        return value === undefined ? [] : [value]
      }),
    [effectiveMap, requests]
  )
  const loading = useAppSelector(selectFirmEffectiveLoading)
  const error = useAppSelector(selectFirmEffectiveError)
  const counts = new Map<string, number>()
  for (const value of values) {
    counts.set(value.autonomyRung, (counts.get(value.autonomyRung) ?? 0) + 1)
  }

  return (
    <section
      className="rounded-lg border border-border bg-card px-4 py-4"
      aria-labelledby="autonomy-ladder"
    >
      <div id="autonomy-ladder">
        <SectionLabel>{t('panels.autonomy.title')}</SectionLabel>
      </div>
      {loading && values.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          {t('catalogue.loading')}
        </p>
      ) : error !== null || values.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          {t('catalogue.effectiveUnavailable')}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {[
            'L1_SUGGESTS',
            'L2_DRAFTS',
            'L3_GUARDED_ACT',
            'L4_ACTS_AND_REPORTS',
          ].map((rung) => (
            <li
              key={rung}
              className="flex items-baseline justify-between gap-3 text-sm"
            >
              <span className="text-foreground" data-rung={rung}>
                {labels.rung(rung)}
              </span>
              <span className="text-muted-foreground">
                {counts.get(rung) ?? 0}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function ReviewPolicyPanel({
  values,
}: {
  values: readonly FirmEffectiveAgentSettings[]
}) {
  const { t } = useTranslation(['agents', 'common'])
  const policies = values.flatMap((value) =>
    value.reviewPolicy === undefined ? [] : [value.reviewPolicy]
  )
  const sample = policies[0]
  return (
    <section
      className="rounded-lg border border-border bg-card px-4 py-4"
      aria-labelledby="review-policy"
    >
      <div id="review-policy">
        <SectionLabel>{t('panels.review.title')}</SectionLabel>
      </div>
      {sample === undefined ? (
        <p className="mt-2 text-sm text-muted-foreground">
          {t('catalogue.effectiveUnavailable')}
        </p>
      ) : (
        <dl className="mt-3 space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt>{t('configuration.fields.namedHumanMerge')}</dt>
            <dd>
              {sample.requiresNamedHumanMerge === true
                ? t('common:values.yes')
                : t('catalogue.unavailable')}
            </dd>
          </div>
          {sample.secondReviewerRule !== undefined && (
            <div className="flex justify-between gap-3">
              <dt>{t('configuration.fields.secondReviewer')}</dt>
              <dd>{sample.secondReviewerRule}</dd>
            </div>
          )}
          {sample.escalationHours !== undefined && (
            <div className="flex justify-between gap-3">
              <dt>{t('configuration.fields.escalation')}</dt>
              <dd>{sample.escalationHours}</dd>
            </div>
          )}
        </dl>
      )}
    </section>
  )
}

export function AgentCataloguePanel({ orgId }: { orgId: string }) {
  const { t } = useTranslation('agents')
  const dispatch = useAppDispatch()
  const agents = useAppSelector(selectAgents)
  const agentsLoading = useAppSelector(selectAgentsLoading)
  const agentsError = useAppSelector(selectAgentsError)
  const descriptors = useAppSelector(selectAgentDescriptors)
  const descriptorCache = useAppSelector(selectDescriptorsCache)
  const descriptorsError = useAppSelector(selectDescriptorsError)
  const effectiveLoading = useAppSelector(selectFirmEffectiveLoading)
  const effectiveError = useAppSelector(selectFirmEffectiveError)
  const requests = useMemo<readonly FirmEffectiveRequest[]>(
    () =>
      agents.flatMap((agent) =>
        AGENT_PHASES.map((phase) => ({ orgId, phase, agentKey: agent.id }))
      ),
    [agents, orgId]
  )
  const effectiveMap = useAppSelector(selectFirmEffectiveMap)
  const effectiveValues = useMemo(
    () =>
      requests.flatMap((request) => {
        const value = effectiveMap[firmEffectiveKeyOf(request)]
        return value === undefined ? [] : [value]
      }),
    [effectiveMap, requests]
  )
  const rows = useMemo(
    () => catalogueRows(agents, descriptors),
    [agents, descriptors]
  )

  useEffect(() => {
    if (orgId !== '') void dispatch(openAgents({ orgId }))
    void dispatch(fetchAgentDescriptors())
  }, [dispatch, orgId])

  useEffect(() => {
    if (requests.length > 0)
      void dispatch(loadFirmEffectiveCollection({ requests }))
  }, [dispatch, requests])

  const retry = () => {
    dispatch(agentsRefetchArmed())
    dispatch(descriptorsRetryClicked())
    void dispatch(openAgents({ orgId }))
    void dispatch(fetchAgentDescriptors())
  }

  const retryEffective = () => {
    dispatch(firmEffectiveRefetchArmed())
    void dispatch(loadFirmEffectiveCollection({ requests }))
  }

  return (
    <>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
        <KpiCard
          label={t('catalogue.configured')}
          figure={
            agentsLoading
              ? '—'
              : agents.filter((agent) => agent.isConfigured).length
          }
          qualifier={t('catalogue.ofAgents', { count: agents.length })}
        />
      </div>

      <section
        className="mt-5 overflow-x-auto rounded-lg border border-border bg-card"
        aria-label={t('catalogue.title')}
      >
        {agentsLoading ? (
          <LoadingPanel label={t('catalogue.loading')} />
        ) : agentsError !== null ? (
          <div className="p-4">
            <ErrorPanel error={agentsError} onRetryClicked={retry} />
          </div>
        ) : agents.length === 0 ? (
          <EmptyPanel
            title={t('catalogue.emptyTitle')}
            body={t('catalogue.empty')}
          />
        ) : (
          <Table>
            <TableCaption className="sr-only">
              {t('catalogue.caption')}
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'pl-3')}>
                  {t('catalogue.columns.agent')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('catalogue.columns.description')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('catalogue.columns.configuration')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('catalogue.columns.autonomy')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('catalogue.columns.runtime')}
                </TableHead>
                <TableHead
                  scope="col"
                  className={cn(HEAD_CLASS, 'pr-3 text-right')}
                >
                  {t('catalogue.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <CatalogueRowView key={row.agent.id} row={row} orgId={orgId} />
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {descriptorsError !== null && !descriptorCache.loading && (
        <DensityNote>{t('catalogue.descriptorUnavailable')}</DensityNote>
      )}
      {effectiveError !== null && !effectiveLoading && (
        <ErrorPanel
          error={{
            status: effectiveError.status,
            backendKey: effectiveError.code,
            message: effectiveError.message,
          }}
          onRetryClicked={retryEffective}
          className="mt-4"
        />
      )}
      <div className="mt-5 grid gap-4 lg:grid-cols-3">
        <AutonomyLadder requests={requests} />
        <ReviewPolicyPanel values={effectiveValues} />
      </div>
    </>
  )
}
